import { Slot } from '@radix-ui/react-slot'
import { clsx } from 'clsx'
import type { CSSProperties, ReactNode } from 'react'

import styles from './Text.module.scss'

interface TextProps {
  align?: 'left' | 'center' | 'right'
  asChild?: boolean
  children: ReactNode
  className?: string
  code?: boolean
  color?: 'green' | 'red' | 'muted' | 'gray700' | 'gray350' | 'orange'
  mb?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  monospace?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
  state?: 'success' | 'danger'
  style?: CSSProperties
  truncate?: boolean
  weight?: 'medium' | 'semibold' | 'bold' | 'bolder'
  wrap?: 'nowrap' | 'balance'
}

export function Text({
  align,
  asChild,
  children,
  className,
  code,
  color,
  mb,
  monospace,
  size,
  style,
  truncate,
  weight,
  wrap,
  ...restProps
}: TextProps) {
  const Component = asChild ? Slot : 'p'

  return (
    <Component
      {...restProps}
      className={clsx(
        {
          // alignments
          [styles.left]: align === 'left',
          [styles.center]: align === 'center',
          [styles.right]: align === 'right',
          // colors
          [styles.success]: color === 'green',
          [styles.danger]: color === 'red',
          [styles.muted]: color === 'muted',
          [styles.gray700]: color === 'gray700',
          [styles.gray350]: color === 'gray350',
          [styles.orange]: color === 'orange',
          // sizes
          [styles.xs]: size === 'xs',
          [styles.sm]: size === 'sm',
          [styles.md]: size === 'md',
          [styles.lg]: size === 'lg',
          [styles.xl]: size === 'xl',
          [styles.xxl]: size === '2xl',
          [styles.xxxl]: size === '3xl',
          // weights
          [styles.medium]: weight === 'medium',
          [styles.semibold]: weight === 'semibold',
          [styles.bold]: weight === 'bold',
          [styles.bolder]: weight === 'bolder',
          // margin bottom
          [styles.mb0]: mb === 0,
          [styles.mb1]: mb === 1,
          [styles.mb2]: mb === 2,
          [styles.mb3]: mb === 3,
          [styles.mb4]: mb === 4,
          [styles.mb5]: mb === 5,
          [styles.mb6]: mb === 6,
          // other
          [styles.truncate]: truncate,
          [styles.monospace]: monospace,
          [styles.code]: code,
          // text wrapping
          [styles.nowrap]: wrap === 'nowrap',
          [styles.balance]: wrap === 'balance',
        },
        className,
      )}
      style={style}
    >
      {children}
    </Component>
  )
}
